import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Typography,
  IconButton,
  Chip,
  Badge,
  Tooltip,
  SwipeableDrawer,
  Divider,
  Stack,
  Paper,
  alpha,
} from "@mui/material";
import {
  Close,
  Info,
  Warning,
  Error as ErrorIcon,
  Notifications,
  NotificationsActive,
  Business,
  Language,
  AccessTime,
  Router,
} from "@mui/icons-material";

import { criticalAlertSx } from "../model/alertStyles";

const AlertItem = ({ alert }) => {
  const getIcon = () => {
    switch (alert.severity) {
      case "error":
        return <ErrorIcon sx={{ color: "#f44336", fontSize: 20 }} />;
      case "warning":
        return <Warning sx={{ color: "#ff9800", fontSize: 20 }} />;
      default:
        return <Info sx={{ color: "#2196f3", fontSize: 20 }} />;
    }
  };

  const getColor = () => {
    switch (alert.severity) {
      case "error":
        return "#f44336";
      case "warning":
        return "#ff9800";
      default:
        return "#2196f3";
    }
  };

  // Formatear fecha para mostrar más limpia
  const formatAlertTime = (timeStr) => {
    if (!timeStr) return "N/A";
    // Si ya viene formateado, mostrarlo
    if (timeStr.includes("/") || timeStr.includes(":")) {
      return timeStr;
    }
    return timeStr;
  };

  return (
    <Paper
      elevation={0}
      sx={{
        mb: 1.5,
        p: 1.5,
        borderRadius: "12px",
        backgroundColor: alpha(getColor(), 0.08),
        borderLeft: `4px solid ${getColor()}`,
        ...criticalAlertSx(alert),

        transition: "all 0.2s ease",
        "&:hover": {
          backgroundColor: alpha(getColor(), 0.15),
          transform: "translateX(-2px)",
        },
      }}
    >
      <Box display="flex" alignItems="flex-start" gap={1.5}>
        <Box flexShrink={0}>{getIcon()}</Box>

        <Box flex={1} minWidth={0}>
          {/* Mensaje de alerta */}
          <Typography
            variant="body2"
            sx={{
              fontWeight: 700,
              fontSize: "1.05rem",
              color: alert.severity === "error" ? "#b42318" : "#243b53",
              lineHeight: 1.4,
              mb: 1,
              wordBreak: "break-word",
            }}
          >
            {alert.message}
          </Typography>

          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mb: 1 }}>
            <Chip size="small" label={alert.priorityText} sx={{ height: 22 }} />
          </Box>
          {/* Información del equipo */}
          <Stack spacing={0.75}>
            <Box display="flex" alignItems="center" gap={1}>
              <Business sx={{ fontSize: 14, color: "text.secondary" }} />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  fontSize: "0.7rem",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {alert.commerce || "Comercio no especificado"}
              </Typography>
            </Box>

            <Box display="flex" alignItems="center" gap={1}>
              <Router sx={{ fontSize: 14, color: "text.secondary" }} />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  fontSize: "0.7rem",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {alert.equipment || "Equipo no especificado"}
              </Typography>
            </Box>

            <Box display="flex" alignItems="center" gap={1}>
              <Language sx={{ fontSize: 14, color: "text.secondary" }} />
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.7rem" }}>
                {alert.ip || "IP no disponible"}
              </Typography>
            </Box>

            <Box display="flex" alignItems="center" gap={1}>
              <AccessTime sx={{ fontSize: 14, color: "text.secondary" }} />
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.7rem" }}>
                {formatAlertTime(alert.time)}
              </Typography>
            </Box>
          </Stack>
        </Box>
      </Box>
    </Paper>
  );
};

AlertItem.propTypes = {
  alert: PropTypes.object.isRequired,
};

const AlertsPanel = ({ title = "Alertas en Tiempo Real", controller }) => {
  const { allAlerts: alerts, socketConnected: isConnected } = controller;

  const [open, setOpen] = useState(false);

  const toggleDrawer = (openState) => () => setOpen(openState);

  // Contar alertas por severidad
  const criticalCount = alerts.filter((a) => a.severity === "error").length;
  const warningCount = alerts.filter((a) => a.severity === "warning").length;
  const infoCount = alerts.filter((a) => a.severity === "info").length;

  return (
    <>
      <Box sx={{ position: "fixed", bottom: 20, right: 20, zIndex: 1100 }}>
        <Badge badgeContent={alerts.length} color="error" overlap="circular">
          <Tooltip title={open ? "Cerrar alertas" : "Ver alertas en tiempo real"}>
            <IconButton
              aria-label={open ? "Cerrar panel de alertas" : "Abrir panel de alertas"}
              onClick={toggleDrawer(!open)}
              sx={{
                ...criticalAlertSx({ severity: criticalCount ? "error" : "info" }),
                backgroundColor: isConnected ? "#2E7D32" : "#9e9e9e",
                color: "white",
                width: 56,
                height: 56,
                boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
                "&:hover": { backgroundColor: isConnected ? "#1B5E20" : "#757575" },
              }}
            >
              <NotificationsActive sx={{ fontSize: 28 }} />
            </IconButton>
          </Tooltip>
        </Badge>
      </Box>

      <SwipeableDrawer
        anchor="right"
        open={open}
        onClose={toggleDrawer(false)}
        onOpen={toggleDrawer(true)}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: 450, md: 500 },
            maxWidth: "100%",
            borderRadius: { xs: 0, sm: "16px 0 0 16px" },
          },
        }}
      >
        <Box
          sx={{
            height: "100%",
            display: "flex",
            flexDirection: "column",
            bgcolor: "background.default",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              p: 2,
              bgcolor: "#193e54",
              color: "white",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexShrink: 0,
            }}
          >
            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
              <Typography variant="h6" fontWeight="bold" sx={{ color: "#fff" }}>
                {title}
              </Typography>
              <Chip
                label={isConnected ? "Tiempo real conectado" : "Tiempo real desconectado"}
                size="small"
                sx={{ bgcolor: isConnected ? "#4caf50" : "#f44336", color: "white", height: 24 }}
              />
            </Box>
            <Box display="flex" gap={0.5}>
              <Tooltip title="Cerrar">
                <IconButton size="small" onClick={toggleDrawer(false)} sx={{ color: "white" }}>
                  <Close fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {/* Resumen de estadísticas */}
          {alerts.length > 0 && (
            <Box
              sx={{
                p: 1.5,
                bgcolor: alpha("#1976d2", 0.05),
                borderBottom: "1px solid",
                borderColor: "divider",
                flexShrink: 0,
              }}
            >
              <Stack direction="row" spacing={2} justifyContent="space-around">
                <Box textAlign="center">
                  <Typography variant="caption" color="text.secondary">
                    Total
                  </Typography>
                  <Typography variant="h6" fontWeight="bold">
                    {alerts.length}
                  </Typography>
                </Box>
                <Box textAlign="center">
                  <Typography variant="caption" color="error.main">
                    Críticas
                  </Typography>
                  <Typography variant="h6" fontWeight="bold" color="error.main">
                    {criticalCount}
                  </Typography>
                </Box>
                <Box textAlign="center">
                  <Typography variant="caption" sx={{ color: "#ff9800" }}>
                    Advertencias
                  </Typography>
                  <Typography variant="h6" fontWeight="bold" sx={{ color: "#ff9800" }}>
                    {warningCount}
                  </Typography>
                </Box>
                <Box textAlign="center">
                  <Typography variant="caption" color="info.main">
                    Información
                  </Typography>
                  <Typography variant="h6" fontWeight="bold" color="info.main">
                    {infoCount}
                  </Typography>
                </Box>
              </Stack>
            </Box>
          )}

          {/* Lista de alertas */}
          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
              p: 2,
              "&::-webkit-scrollbar": {
                width: "8px",
              },
              "&::-webkit-scrollbar-track": {
                background: "#f1f1f1",
                borderRadius: "4px",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "#c1c1c1",
                borderRadius: "4px",
                "&:hover": {
                  background: "#a8a8a8",
                },
              },
            }}
          >
            {alerts.length === 0 ? (
              <Box textAlign="center" py={6}>
                <Notifications sx={{ fontSize: 64, color: "text.disabled", mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  {isConnected ? "No hay alertas nuevas" : "Esperando conexión..."}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {isConnected
                    ? "Las nuevas alertas aparecerán aquí automáticamente"
                    : "Conectando al servidor de alertas..."}
                </Typography>
              </Box>
            ) : (
              <>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="caption" color="text.secondary">
                    {alerts.length} alerta{alerts.length !== 1 ? "s" : ""} reportadas por el sistema
                  </Typography>
                </Box>
                {alerts.map((alert) => (
                  <AlertItem key={alert.id} alert={alert} />
                ))}
              </>
            )}
          </Box>

          {/* Footer */}
          <Divider />
          <Box sx={{ p: 1.5, bgcolor: "grey.50", textAlign: "center", flexShrink: 0 }}>
            <Stack direction="row" spacing={2} justifyContent="center">
              <Typography variant="caption">
                {isConnected ? "🟢 Conectado" : "🔴 Desconectado"}
              </Typography>
            </Stack>
          </Box>
        </Box>
      </SwipeableDrawer>
    </>
  );
};

AlertsPanel.propTypes = { title: PropTypes.string, controller: PropTypes.object.isRequired };
AlertsPanel.defaultProps = { title: "Alertas en Tiempo Real" };

export default AlertsPanel;
